import { memo } from 'react';
import {
  BadgeCheck,
  Eye,
  ExternalLink,
  Heart,
  MessageSquare,
  Pin,
  Quote,
  Repeat2,
  Reply,
  Trash2,
} from 'lucide-react';
import { POST_TYPE_LABELS, type PostDTO } from '@bb/shared';
import { cn } from '@/lib/cn';
import { metric, relative, utcStamp } from '@/lib/format';
import { Badge } from '@/components/ui/Badge';
import { IconButton } from '@/components/ui/Button';
import { CategoryBadge, PendingBadge } from './CategoryBadge';
import { PostMedia } from './PostMedia';
import { PostText } from './PostText';

const TYPE_ICON = {
  ORIGINAL: null,
  REPLY: Reply,
  REPOST: Repeat2,
  QUOTE: Quote,
} as const;

function Avatar({ post }: { post: PostDTO }) {
  const initials = post.author.displayName.replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase();

  if (post.author.avatarUrl) {
    return (
      <img
        src={post.author.avatarUrl}
        alt=""
        loading="lazy"
        className="size-7 shrink-0 rounded-sharp border border-line object-cover"
      />
    );
  }

  return (
    <div
      className="flex size-7 shrink-0 items-center justify-center rounded-sharp border font-mono text-[10px] font-medium"
      style={{
        borderColor: post.author.color ?? 'var(--color-line-bright)',
        color: post.author.color ?? 'var(--color-ink-dim)',
        backgroundColor: 'var(--color-panel-alt)',
      }}
    >
      {initials || '??'}
    </div>
  );
}

function Metric({ icon: Icon, value }: { icon: typeof Heart; value: number }) {
  if (value === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 text-ink-faint">
      <Icon size={12} strokeWidth={1.75} />
      <span className="data-mono text-[11px]">{metric(value)}</span>
    </span>
  );
}

export type PostCardProps = {
  post: PostDTO;
  /** Set for posts that arrived over the live stream, to play the arrival cue. */
  isNew?: boolean;
  onTogglePin: (post: PostDTO) => void;
  onToggleRead: (post: PostDTO) => void;
};

export const PostCard = memo(function PostCard({
  post,
  isNew = false,
  onTogglePin,
  onToggleRead,
}: PostCardProps) {
  const TypeIcon = TYPE_ICON[post.type];

  return (
    <article
      className={cn(
        'group flex flex-col rounded-sharp border border-line border-l-2 bg-panel/60 p-3 transition-colors hover:border-line-bright hover:bg-panel',
        post.isPinned ? 'border-l-amber' : 'border-l-line',
        !post.isRead && 'bg-panel-alt/60',
        // A withdrawn post stays fully legible — it is the reason the archive
        // exists — but is tinted so it never reads as still standing.
        post.deletedAt && 'border-l-alert bg-alert-dim/25',
        post.analysis?.category === 'EMERGENCY' && !post.deletedAt && 'border-l-alert',
        isNew && 'animate-arrive',
      )}
    >
      {/* The avatar sits in the header rather than in a left column: in a card
          this narrow, a 44px gutter running the full height would cost the text
          a quarter of its line length. */}
      <header className="flex items-center gap-2">
        <Avatar post={post} />

        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <div className="flex items-center gap-1">
            <span className="truncate text-[13px] font-medium text-ink">
              {post.author.displayName}
            </span>
            {post.author.verified ? (
              <BadgeCheck size={12} className="shrink-0 text-info" strokeWidth={2} />
            ) : null}
          </div>
          <span className="data-mono truncate text-[11px] text-ink-dim">
            @{post.author.handle}
          </span>
        </div>

        <span
          className="data-mono shrink-0 self-start text-[10px] text-ink-faint"
          title={utcStamp(post.postedAt)}
        >
          {relative(post.postedAt)}
        </span>
      </header>

      <div className="mt-2 flex flex-wrap items-center gap-1">
        {post.analysis ? <CategoryBadge analysis={post.analysis} /> : <PendingBadge />}

        {post.type !== 'ORIGINAL' ? (
          <Badge tone="neutral">
            {TypeIcon ? <TypeIcon size={9} strokeWidth={2} /> : null}
            {POST_TYPE_LABELS[post.type]}
          </Badge>
        ) : null}

        {post.deletedAt ? (
          <Badge tone="alert" className="gap-1">
            <Trash2 size={9} strokeWidth={2} />
            Deleted
          </Badge>
        ) : null}
      </div>

      <div className="mt-2">
        <PostText text={post.text} entities={post.entities} />
      </div>

      <PostMedia media={post.media} />

      {post.tags.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {post.tags.map((tag) => (
            <span
              key={tag.id}
              className="rounded-sharp border px-1.5 py-px font-mono text-[10px] uppercase tracking-[0.08em]"
              style={{ borderColor: `${tag.color}66`, color: tag.color }}
            >
              {tag.name}
            </span>
          ))}
        </div>
      ) : null}

      {/* Pushed to the bottom so the action row lines up across a grid row even
          when the cards above it differ in height. */}
      <div className="mt-auto flex items-center gap-3 pt-2.5">
        <Metric icon={Heart} value={post.metrics.likes} />
        <Metric icon={Repeat2} value={post.metrics.reposts} />
        <Metric icon={MessageSquare} value={post.metrics.replies} />
        <Metric icon={Eye} value={post.metrics.views} />

        <div className="ml-auto flex items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <IconButton
            active={post.isPinned}
            onClick={() => onTogglePin(post)}
            title={post.isPinned ? 'Unpin' : 'Pin'}
            aria-label={post.isPinned ? 'Unpin post' : 'Pin post'}
          >
            <Pin size={13} strokeWidth={1.75} />
          </IconButton>
          <IconButton
            active={post.isRead}
            onClick={() => onToggleRead(post)}
            title={post.isRead ? 'Mark unread' : 'Mark read'}
            aria-label={post.isRead ? 'Mark unread' : 'Mark read'}
          >
            <Eye size={13} strokeWidth={1.75} />
          </IconButton>
          <a
            href={post.url}
            target="_blank"
            rel="noreferrer noopener"
            title="Open on X"
            aria-label="Open on X"
            className="inline-flex size-7 items-center justify-center rounded-sharp border border-transparent text-ink-faint transition-colors hover:border-line-bright hover:bg-raised hover:text-ink"
          >
            <ExternalLink size={13} strokeWidth={1.75} />
          </a>
        </div>
      </div>
    </article>
  );
});
